package com.nonopn.skydome.common.app.navigation

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material.icons.Icons
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import com.nonopn.icons.MapLocation
import com.nonopn.skydome.common.app.api.Mode
import com.nonopn.skydome.common.app.api.impl.ThunderDetectionEstimate
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.pages.estimate.EstimateScreen
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.menu_admin_estimate
import com.nonopn.skywatcher.design.appbar.AppBarState
import com.nonopn.skywatcher.design.widgets.MenuItem
import eu.codlab.navigation.NavTypeWrapper
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.NavigateToStack
import eu.codlab.navigation.NavigateWithNavOptions
import eu.codlab.navigation.Route
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.navigation.RouterParameters
import kotlinx.serialization.Serializable
import kotlin.reflect.KType
import kotlin.reflect.typeOf

@Serializable
data class RouteEstimateExistingAdmin(
    val uuid: String,
    // val estimate: ThunderDetectionEstimate
) : RouteParameterTo

object RouterEstimateExistingAdmin :
    RouterParameters<RouteEstimateExistingAdmin>(RouteEstimateExistingAdmin::class) {
    override val typeMap: Map<KType, NavTypeWrapper<Any>>
        get() = mapOf(typeOf<ThunderDetectionEstimate>() to RouteEstimateExistingParametersType as NavTypeWrapper<Any>)

    override fun routeFrom(parameters: RouteEstimateExistingAdmin) =
        "/admin/estimate/${parameters.uuid}"

    @Composable
    override fun Show(parameters: RouteEstimateExistingAdmin) {
        RouteEstimateExistingAdminImpl(parameters).scene()
    }

    override fun navigateTo(instance: RouteEstimateExistingAdmin) = NavigateTo(
        route = instance,
        stack = NavigateToStack(
            // Keep the list (or the map) in the stack so that "back" returns to it
            popBackStack = false,
            options = NavigateWithNavOptions(
                launchSingleTop = true
            )
        )
    )

    override fun isCurrentRoute(routeParameterTo: RouteParameterTo?) =
        null != routeParameterTo && routeParameterTo is RouteEstimateExistingAdmin

    override fun isMatching(route: String) = route.startsWith("/admin/estimate/")

    // Direct entry (link, reload): the uuid is the last segment of "/admin/estimate/<uuid>"
    override fun navigateFrom(path: String) =
        RouteEstimateExistingAdmin(uuid = estimateUuidFromAdminPath(path))
}

class RouteEstimateExistingAdminImpl(params: RouteEstimateExistingAdmin) :
    Route<RouteEstimateExistingAdmin>(
        route = "/admin/estimate/${params.uuid}",
        params = params,
    ) {
    @Composable
    override fun scene() {
        val appModel: AppModel = LocalApp.current
        val state by appModel.states.collectAsState()
        val estimate = appModel.estimate(params.uuid)

        // Not an admin account, or a direct entry (the estimates cache is empty): back to a safe screen
        if (state.apiProvider?.apiAdmin == null || estimate == null) {
            LaunchedEffect(Unit) { appModel.leaveAdminRoute() }
            return
        }

        LaunchedEffect(Unit) {
            appModel.ensureAdminMode()
            appModel.markCurrentRoute(params)
            onEntryIsActive(appModel, emptyList())
        }

        EstimateScreen(
            modifier = Modifier.fillMaxSize(),
            appModel = appModel,
            mode = Mode.Admin,
            existingEstimate = estimate
        )
    }

    private fun onEntryIsActive(
        appModel: AppModel,
        defaultActions: List<MenuItem>,
    ) {
        appModel.setAppBarState(
            AppBarState.Localized(
                title = Res.string.menu_admin_estimate,
                defaultActions + MenuItem.MenuItemSimple(
                    imageVector = Icons.Rounded.MapLocation,
                    onClick = { appModel.show(RouterMapAdmin.navigateToKeepingBack()) }
                )
            )
        )
    }
}

private val RouteEstimateExistingParametersType = NavTypeWrapper(
    serializer = ThunderDetectionEstimate.serializer(),
    deserialize = ThunderDetectionEstimate.serializer()
)
